import { type FC, type MouseEventHandler, type ReactNode, useState } from "react";
import Modal from "~/common/components/Modal";
import { Button, Stack } from "~/common/mui";
import AtcProductInfo from "~/features/addToCart/components/v2/AtcProductInfo/AtcProductInfo";
import { CloseBtn } from "~/features/addToCart/components/AtcCloseButton";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { resolveCartPageUrl } from "~/utils/url/urlUtils";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import { IconType } from "~/common/components/icon/IconType";
import {
  setAlreadyAddedToCart,
  updatePriceReference,
  updateQuantity,
} from "~/features/configurator/Linear/redux/actions";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { LinearPopupState } from "~/features/configurator/Linear/constants";
import {
  fakeDescriptorFromProduct,
  getNetPrice,
  makeDescriptorFromFullState,
} from "~/domains/product/utils/productDescriptorUtils";
import { useLinearPrice } from "~/features/configurator/Linear/components/Substeps/useLinearPrice";
import { useTrackLCAddToCartClick } from "~/features/configurator/Linear/hooks/useTrackLCClicks";
import { selectedPriceReference } from "~/features/configurator/common/components/ConfigurationSummarySubstep";
import { useTranslation } from "react-i18next";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import { useLinearProductInfo } from "~/features/configurator/Linear/redux/useLinearProductInfo";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import type { AddToCartApiItem } from "~/features/cart/cart/api/AddToCart";
import type { NetPriceData } from "~/domains/pricing/types/PriceData";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import AtcProductPriceInfo from "~/features/addToCart/components/v2/AtcProductPriceInfo/AtcProductPriceInfo";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import classNames from "classnames";

interface Props {
  onClose: () => void;
}

const LinearATCPopup: FC<Props> = (props: Props) => {
  const { onClose } = props;
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const dispatch = useDispatch();

  const [state, setState] = useState(LinearPopupState.INITIAL);

  const linearState = useSelector((state: RootState) => state.linearConfigurator);

  const productInfo = useLinearProductInfo();

  const quantity = useSelector(getQuantity);

  const trackClick = useTrackLCAddToCartClick();

  const priceReference = useSelector(selectedPriceReference);

  const { priceInfo, isLoadingPrice } = useLinearPrice({
    priceReference: priceReference,
  });
  const productDescriptor = makeDescriptorFromFullState(
    productInfo,
    { state: isLoadingPrice ? "loading" : "loaded", data: priceInfo ? [priceInfo] : [] },
    undefined,
    priceReference,
  );
  const price = getNetPrice({
    totalQuantity: quantity,
    packCode: "",
    product: productDescriptor,
  });

  const isInState = (...validStates: LinearPopupState[]) => {
    const result = validStates.find((el) => el === state);
    return result !== undefined;
  };

  const onAddToCart = async () => {
    const transformLinearToATCItem = (product: ProductDescriptor, price: NetPriceData): AddToCartApiItem => {
      return {
        productCode: product.base.code,
        packageCode: "XX",
        quantity: quantity,
        netPrice: price.value,
        priceUnit: price.unit,
        selectedDeliveryDateIsManual: false,
        fifteenDigit: false,
        isRestrictedPackage: false,
        surcharge: 0,
        originCountry: "", // no package code, no fifteen digit, no origin country
        tariffSurchargeApplicable: false,
        isPMA: false,
      };
    };

    setState(LinearPopupState.SENDING);
    let success: boolean;
    let newConfigId: string | undefined = undefined;
    try {
      const productCode = linearState.productCode;
      const addToCartApi = getConfiguratorDefinition(productCode).apiCalls.addToCart;

      newConfigId = await addToCartApi(linearState, priceInfo);
      success = true;
      window.SCHA.updateMinicart?.();
      trackClick();
    } catch (e) {
      console.error(e);
      success = false;
    }
    if (success) {
      dispatch(setAlreadyAddedToCart(newConfigId));
      onClose();
      openModal("add-to-cart-dialog", (close) => (
        <CartSuccessModal
          linearConfig={productDescriptor.base}
          formData={[transformLinearToATCItem(productDescriptor, price)]}
          onClose={close}
          page="LC"
        />
      ));
    } else {
      setState(LinearPopupState.ERROR);
    }
  };

  const finalState = isInState(LinearPopupState.SUCCESS, LinearPopupState.ERROR);
  const primaryButtonLabel = t(
    `page.linear.addToCart.button.${finalState ? "go" : "add"}To${userIsSalesman() ? "Quote" : "Cart"}`,
  );
  const primaryButtonUrl = finalState ? resolveCartPageUrl() : undefined;

  return (
    <Modal
      title={t("addToCartPopup.title")}
      onClose={onClose}
      footerChildren={
        <Stack direction={"row"} gap={1}>
          {isInState(LinearPopupState.INITIAL, LinearPopupState.SENDING) ? (
            <CloseBtn onClick={onClose} disabled={isInState(LinearPopupState.SENDING)}>
              {t("cancel")}
            </CloseBtn>
          ) : (
            <CloseBtn onClick={onClose}>{t("close")}</CloseBtn>
          )}

          <AddToCartButton
            disabled={isInState(LinearPopupState.SENDING, LinearPopupState.ERROR)}
            onClick={isInState(LinearPopupState.INITIAL) ? onAddToCart : undefined}
            href={primaryButtonUrl}
          >
            {primaryButtonLabel}
          </AddToCartButton>
        </Stack>
      }
    >
      <div className="scha__atc-popup scha__atc-popup-continer">
        <div className="scha__atc-popup--column-info">
          <AtcProductInfo descriptor={fakeDescriptorFromProduct(productInfo)} />
        </div>
        <div className="scha__atc-popup--column-packaging__wrapper scha__atc-popup--column-packaging">
          <div className={classNames(isDesktop && "flex-1")}>
            {isInState(LinearPopupState.INITIAL) && <p>{t("page.linear.addToCart.text.initial")}</p>}
            {isInState(LinearPopupState.SENDING) && (
              <div className="scha__atc-popup--column-packaging-loading">
                <CircleSpinner />
              </div>
            )}
            {isInState(LinearPopupState.ERROR) && (
              <Alert severity="error" className="scha-atc__delivery-disclaimer no-bottom">
                {t("page.linear.addToCart.text.error")}
              </Alert>
            )}
          </div>
          <div>
            <AtcProductPriceInfo
              descriptor={fakeDescriptorFromProduct(productInfo)}
              quantity={quantity}
              price={price ?? null}
              priceUnit={1}
              onChange={(newQuantity: number) => dispatch(updateQuantity(newQuantity))}
              onPriceReferenceChange={(newValue: string | undefined) => dispatch(updatePriceReference(newValue))}
              priceReference={priceReference}
              isPriceLoading={isLoadingPrice}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

const getQuantity = (state: RootState) => state.linearConfigurator.quantity;

export default LinearATCPopup;

interface ButtonProps {
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  href?: string;
  disabled?: boolean;
  children?: ReactNode;
}

const AddToCartButton: FC<ButtonProps> = (props) => (
  <Button
    startIcon={<DefaultButtonIcon type={IconType.Cart} />}
    disabled={window.SCHA.config.disableAllWS || (props.disabled == null ? false : props.disabled)}
    {...props}
  />
);
